import { useTranslation } from "react-i18next";
import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Divider, Paper, Stack, Typography } from "@mui/material";
import * as React from "react";
import {useCallback, useContext, useEffect, useState} from "react";
import { useNavigate, useParams } from "react-router-dom";
import dayjs from "dayjs";
import { useMutation } from "@tanstack/react-query";
import { AxiosError } from "axios";
import SectionHeader from "../../../../util/component/SectionHeader";
import ViewSimpleProperty from "./_components/ViewSimpleProperty";
import ViewStructuredProperty from "./_components/ViewStructuredProperty";
import Note from "../view/tabs-manager/tabs/notes/components/Note";
import { GlobalContext } from "../../../../util/state/GlobalContextProvider";
import { setSnackbar } from "../../../../util/state/globalAction";
import { snackbarTypes } from "../../../../util/state/globalVariables";
import { deleteBookingDisambiguationJimpi, saveBookingDisambiguationJimpi, getDisambiguation, IDisambiguation } from "../../api/services";
import { setDecimalSeparator } from "../../utils";
import DialogCloseButton from "../../components/DialogCloseButton";
import useDialog from "../../hooks/useDialog";
import CustomLoader from "../../../../util/component/CustomLoader";
import { RouteParams } from "../../types";

/**
 * Page for view disambiguation Jimpi details
 */
const ViewDisambiguation = () => {

    const { t, i18n } = useTranslation('booking');

    const { id } = useParams<RouteParams>();

    let navigate = useNavigate();
    const acceptDialog = useDialog();
    const rejectDialog = useDialog();

    const {dispatchGlobalData} = useContext(GlobalContext);

    const [booking, setBooking] = useState<any>(null);
    const [bookingDisambiguation, setBookingDisambiguation] = useState<any>(null);

    const saveBookingDisambiguationMutation = useMutation(saveBookingDisambiguationJimpi);
    const deleteBookingDisambiguationMutation = useMutation(deleteBookingDisambiguationJimpi);

    const getBookingDisambiguation = useMutation(({
        mutationFn: () => getDisambiguation(id),
        onSuccess: (response: IDisambiguation) => {
            setAncillariesField(response);

            // set car group JIMPI reservation
            const bookingDisambiguationCarGroup = [
                {
                    label: t('label.acriss'),
                    value: response.bookingDisambiguation.carGroup.acrissCode,
                    error: response.bookingDisambiguation?.carGroup.acrissCode?.toLowerCase() !== response.booking?.carGroup.acrissCode?.toLowerCase()
                },
                {
                    label: t('label.sipp'),
                    value: response.bookingDisambiguation.carGroup.sippCode,
                    error: response.bookingDisambiguation.carGroup.sippCode?.toLowerCase() !== response.booking.carGroup.sippCode?.toLowerCase()
                }];

            // set fisrt driver JIMPI reservation
            const bookingDisambiguationFirstDriver = [
                {
                    label: t('label.firstName'),
                    value: response.bookingDisambiguation.firstDriver.name,
                    error: response.bookingDisambiguation.firstDriver.name?.toLowerCase() !== response.booking.firstDriver.name?.toLowerCase()
                },
                {
                    label: t('label.lastName'),
                    value: response.bookingDisambiguation.firstDriver.surname,
                    error: response.bookingDisambiguation.firstDriver.surname?.toLowerCase() !== response.booking.firstDriver.surname?.toLowerCase()
                }];

            //set car group booking
            const bookingCarGroup = [
                {
                    label: t('label.acriss'),
                    value: response.booking?.carGroup.acrissCode,
                    error: response.bookingDisambiguation?.carGroup.acrissCode?.toLowerCase() !== response.booking?.carGroup.acrissCode?.toLowerCase()
                },
                {
                    label: t('label.sipp'),
                    value: response.booking?.carGroup.sippCode,
                    error: response.bookingDisambiguation?.carGroup.sippCode?.toLowerCase() !== response.booking?.carGroup.sippCode?.toLowerCase()
                }];

            //set first driver booking
            const bookingFirstDriver = [
                {
                    label: t('label.firstName'),
                    value: response.booking?.firstDriver.name,
                    error: response.bookingDisambiguation?.firstDriver.name?.toLowerCase() !== response.booking?.firstDriver.name?.toLowerCase()
                },
                {
                    label: t('label.lastName'),
                    value: response.booking?.firstDriver.surname,
                    error: response.bookingDisambiguation?.firstDriver.surname?.toLowerCase() !== response.booking?.firstDriver.surname?.toLowerCase()
                }];

            response.booking.requestCreationDate = !!response.booking?.requestCreationDate ? dayjs(response.booking?.requestCreationDate).format('DD-MM-YYYY HH:mm') : '';
            response.bookingDisambiguation.requestCreationDate = !!response.bookingDisambiguation?.requestCreationDate ? dayjs(response.bookingDisambiguation?.requestCreationDate).format('DD-MM-YYYY HH:mm') : '';
            response.booking.requestUpdateDate = !!response.booking?.requestUpdateDate ? dayjs(response.booking?.requestUpdateDate).format('DD-MM-YYYY HH:mm') : '';
            response.bookingDisambiguation.requestUpdateDate = !!response.bookingDisambiguation?.requestUpdateDate ? dayjs(response.bookingDisambiguation?.requestUpdateDate).format('DD-MM-YYYY HH:mm') : '';
            response.booking.pickupDateLocalISO = !!response.booking?.pickupDateLocalISO ? dayjs(response.booking?.pickupDateLocalISO).format('DD-MM-YYYY HH:mm') : '';
            response.bookingDisambiguation.pickupDateLocalISO = !!response.bookingDisambiguation?.pickupDateLocalISO ? dayjs(response.bookingDisambiguation?.pickupDateLocalISO).format('DD-MM-YYYY HH:mm') : '';
            response.booking.dropoffDateLocalISO = !!response.booking?.dropoffDateLocalISO ? dayjs(response.booking?.dropoffDateLocalISO).format('DD-MM-YYYY HH:mm') : '';
            response.bookingDisambiguation.dropoffDateLocalISO = !!response.bookingDisambiguation?.dropoffDateLocalISO ? dayjs(response.bookingDisambiguation?.dropoffDateLocalISO).format('DD-MM-YYYY HH:mm') : '';
            response.booking.status = !!response.booking.status ? `status.${response.booking?.status}` : '';
            response.booking.subStatus = !!response.booking.subStatus ? `substatus.${response.booking?.subStatus}` :  '';
            response.bookingDisambiguation.status = !!response.bookingDisambiguation.status ? `status.${response.bookingDisambiguation?.status}` : '';
            response.bookingDisambiguation.subStatus = !!response.bookingDisambiguation.subStatus ? `substatus.${response.bookingDisambiguation?.subStatus}` :  '';
            response.booking.carGroup = bookingCarGroup;
            response.booking.firstDriver = bookingFirstDriver;
            response.bookingDisambiguation.carGroup = bookingDisambiguationCarGroup;
            response.bookingDisambiguation.firstDriver = bookingDisambiguationFirstDriver;

            setBooking(response.booking);
            setBookingDisambiguation(response.bookingDisambiguation);
        },
        onError: () => {
            dispatchGlobalData({
                type: setSnackbar,
                payload: {
                    snackbar: {
                        message: t('message.error.default'),
                        type: snackbarTypes.error,
                        show: true,
                        autoClose: true,
                    }
                }
            });
        }
    }));

    // set ancillaries field for booking and JIMPI reservation
    // Importante: sulle ancillaries vengono effettuati i controlli sulla property label come definito con De Feo
    const setAncillariesField = (value: IDisambiguation) => {
        let newBooking = [];
        let newDisambiguation = [];

        const labelDisambiguation = value.bookingDisambiguation.ancillaries.map((elem: any) => {return elem.label});
        const labelBooking = value.booking.ancillaries.map((elem: any) => {return elem.label});

        // add empty record form disambiguation booking
        for(let i = 0; i < value.bookingDisambiguation.ancillaries.length; i++) {
            if(!labelBooking.includes(value.bookingDisambiguation.ancillaries[i].label)){
                newBooking.push({
                    label: value.bookingDisambiguation.ancillaries[i].label,
                    value: null,
                    error: true
                });
                newDisambiguation.push({
                    label: value.bookingDisambiguation.ancillaries[i].label,
                    value: value.bookingDisambiguation.ancillaries[i].units,
                    error: true
                });
            } else {
                const index = value.booking.ancillaries.findIndex((elem: any) => elem.label === value.bookingDisambiguation.ancillaries[i].label);
                newDisambiguation.push({
                    label: value.bookingDisambiguation.ancillaries[i].label,
                    value: value.bookingDisambiguation.ancillaries[i].units,
                    error: value.booking.ancillaries[index].units !== value.bookingDisambiguation.ancillaries[i].units
                });
                newBooking.push({
                    label: value.booking.ancillaries[index].label,
                    value: value.booking.ancillaries[index].units,
                    error: value.booking.ancillaries[index].units !== value.bookingDisambiguation.ancillaries[i].units
                });
            }
        }

        // add empty record form booking
        for(let i = 0; i < value.booking.ancillaries.length; i++) {
            if(!labelDisambiguation.includes(value.booking.ancillaries[i].label)){
                newDisambiguation.push({
                    label: value.booking.ancillaries[i].label,
                    value: null,
                    error: true
                });
                newBooking.push({
                    label: value.booking.ancillaries[i].label,
                    value: value.booking.ancillaries[i].units,
                    error: true
                });
            }
        }

        newBooking.sort((a: any, b: any) => a.label.localeCompare(b.label));
        newDisambiguation.sort((a: any, b: any) => a.label.localeCompare(b.label));

        value.booking.ancillaries = newBooking;
        value.bookingDisambiguation.ancillaries = newDisambiguation;
    }

    const handleReject = () => {rejectDialog.handleShow()};

    const handleAccept = () => {acceptDialog.handleShow()};

    /** Accept disambiguation **/
    const handleApplyActionClick = useCallback(() => {
        saveBookingDisambiguationMutation.mutateAsync(
            id
        ).then((response: any) => {
            acceptDialog.handleClose();
            dispatchGlobalData({
                type: setSnackbar,
                payload: {
                    snackbar: {
                        message: t('message.success.default'),
                        type: snackbarTypes.success,
                        show: true,
                        autoClose: true,
                    },
                },
            });
            navigate(`/booking/view/${response.id}`);
        })
            .catch((err: AxiosError) => {
                acceptDialog.handleClose();
                dispatchGlobalData({
                    type: setSnackbar,
                    payload: {
                        snackbar: {
                            message: t('message.error.default'),
                            type: snackbarTypes.error,
                            show: true,
                            autoClose: true,
                        }
                    }
                });
            });
    }, [id]);

    /** Reject disambiguation **/
    const handleRejectClick = useCallback(() => {
        deleteBookingDisambiguationMutation.mutateAsync(
            id
        ).then(() => {
            rejectDialog.handleClose();
            dispatchGlobalData({
                type: setSnackbar,
                payload: {
                    snackbar: {
                        message: t('message.success.default'),
                        type: snackbarTypes.success,
                        show: true,
                        autoClose: true,
                    },
                },
            });
            navigate(`/booking/view/${booking.id}`);
        }).catch((err: AxiosError) => {
            rejectDialog.handleClose();
            dispatchGlobalData({
                type: setSnackbar,
                payload: {
                    snackbar: {
                        message: t('message.error.default'),
                        type: snackbarTypes.error,
                        show: true,
                        autoClose: true,
                    }
                }
            });
        });
    }, [id, booking, bookingDisambiguation]);

    // get data from wsRest
    useEffect(() => {
        getBookingDisambiguation.mutateAsync();
    }, [id])

    return (
        <>
            {/* Header */}
            <SectionHeader title={t('label.jimpiDisambiguationViewHeaderTitle')} subtitle={
                <Stack direction="row" alignItems="center" columnGap={3}>
                    <Stack direction="row" alignItems="end" columnGap={1}>
                        <Typography variant="bodyLarge">{t('label.jimpiDisambiguationViewHeaderSubtitle')}</Typography>
                    </Stack>

                </Stack>
            }/>

            {/* Button Action */}
            <Stack direction="row" justifyContent="end" alignItems="center" columnGap={2} p={2}>
                <Button disabled={!booking || !bookingDisambiguation} variant="outlined" onClick={handleReject}>
                    {t("label.jimpiDisambiguationViewRejectAction")}
                </Button>
                <Button disabled={!booking || !bookingDisambiguation} onClick={handleAccept}>
                    {t("label.jimpiDisambiguationViewAcceptAction")}
                </Button>
            </Stack>

            {/* Content Disambiguation */}
            <Stack py={2} direction="row" justifyContent="center">
                <Paper>
                    <Stack direction="row" p={4} overflow="auto">
                        {/* section current booking */}
                        <Stack rowGap={.7}>
                            <Typography variant="h5" mb={2} color="primary" ml={1}>
                                {t('label.jimpiDisambiguationViewCurrentBooking')}
                            </Typography>

                            {/* Renteway number */}
                            <ViewSimpleProperty
                                value={booking?.rentwayNumber}
                                error={booking?.rentwayNumber !== bookingDisambiguation?.rentwayNumber}
                                label={t('label.rentwayNumber')}
                            />

                            {/* Dossier Number */}
                            <ViewSimpleProperty
                                value={booking?.dossierNumber}
                                error={booking?.dossierNumber?.toLowerCase() !== bookingDisambiguation?.dossierNumber?.toLowerCase()}
                                label={t('label.dossierNumber')}
                            />
                            {/* Status - subStatus */}
                            <ViewSimpleProperty
                                value={ (!!booking?.status ? t(`${booking?.status}`) : '' ) + (!!booking?.subStatus ? ' - ' + t(`${booking?.subStatus}`) : '')}
                                error={booking?.status !== bookingDisambiguation?.status || booking?.subStatus !== bookingDisambiguation?.subStatus}
                                label={t('label.status')}
                            />
                            {/* Creation Date Time */}
                            <ViewSimpleProperty
                                value={booking?.requestCreationDate}
                                error={booking?.requestCreationDate !== bookingDisambiguation?.requestCreationDate}
                                label={t('label.creationDate')}
                            />

                            {/* Total to be paid */}
                            <ViewSimpleProperty
                                value={booking?.requestUpdateDate}
                                error={booking?.requestUpdateDate !== bookingDisambiguation?.requestUpdateDate}
                                label={t('label.updateDateTime')}
                            />

                            {/* Pickup location */}
                            <ViewSimpleProperty
                                value={booking?.pickupLocation?.label}
                                error={booking?.pickupLocation?.label !== bookingDisambiguation?.pickupLocation?.label}
                                label={t('label.pickupLocation')}
                            />

                            {/* Pickup date */}
                            <ViewSimpleProperty
                                value={booking?.pickupDateLocalISO}
                                error={booking?.pickupDateLocalISO !== bookingDisambiguation?.pickupDateLocalISO}
                                label={t('label.pickupDateTime')}
                            />

                            {/* Dropoff location */}
                            <ViewSimpleProperty
                                value={booking?.dropoffLocation?.label}
                                error={booking?.dropoffLocation?.label !== bookingDisambiguation?.dropoffLocation?.label}
                                label={t('label.dropoffLocation')}
                            />

                            {/* Dropoff date */}
                            <ViewSimpleProperty
                                value={booking?.dropoffDateLocalISO}
                                error={booking?.dropoffDateLocalISO !== bookingDisambiguation?.dropoffDateLocalISO}
                                label={t('label.dropoffDateTime')}
                            />

                            {/* Total Rental Days */}
                            <ViewSimpleProperty
                                value={booking?.totalRentalDays}
                                error={booking?.totalRentalDays !== bookingDisambiguation?.totalRentalDays}
                                label={t('label.totalRentalDays')}
                            />

                            {/* Flight Number */}
                            <ViewSimpleProperty
                                value={booking?.flightNumber}
                                error={booking?.flightNumber !== bookingDisambiguation?.flightNumber}
                                label={t('label.flightNumber')}
                            />

                            {/* Mileage Included */}
                            <ViewSimpleProperty
                                value={booking?.mileageIncluded}
                                error={booking?.mileageIncluded !== bookingDisambiguation?.mileageIncluded}
                                label={t('label.mileageIncluded')}
                            />

                            {/* Total to be paid */}
                            <ViewSimpleProperty

                                value={(!!booking?.totalToBePaid ? setDecimalSeparator(((parseInt(booking.totalToBePaid) ?? 0) / 100).toFixed(2), i18n.language) : '0') + ' €'}
                                error={booking?.totalToBePaid !== bookingDisambiguation?.totalToBePaid}
                                label={t('label.totalToBePaid')}
                            />

                            <ViewStructuredProperty
                                    value={booking?.ancillaries}
                                    label={t('label.ancillaries')}
                            />

                            <ViewStructuredProperty
                                value={booking?.firstDriver}
                                label={t('label.mainDriver')}
                            />

                            <ViewStructuredProperty
                                value={booking?.carGroup}
                                label={t('label.carGroup')}
                            />

                            {/* Notes*/}
                            <Stack px={1}>
                                <Typography>
                                    {t('label.notes')}
                                </Typography>

                                {
                                    booking?.notes
                                        ?.map((n: any, index: any) => (
                                            <Stack  direction="column" pl={4} pt={1}
                                                   sx={{maxWidth: "490px"}}>
                                                <Note key={index} value={n}/>
                                            </Stack>
                                        ))
                                        .reverse()
                                }
                            </Stack>
                        </Stack>

                        <Divider
                            orientation="vertical"
                            flexItem
                            sx={{mx: 4}}
                        />

                        {/* section JIMPI reservation */}
                        <Stack rowGap={.7}>
                            <Typography variant="h5" mb={2} color="primary" ml={1}>
                                {t('label.jimpiDisambiguationViewJimpiReservation')}
                            </Typography>
                            {/* Renteway number */}
                            <ViewSimpleProperty
                                value={bookingDisambiguation?.rentwayNumber}
                                error={booking?.rentwayNumber !== bookingDisambiguation?.rentwayNumber}
                                label={t('label.rentwayNumber')}
                            />

                            {/* Dossier Number */}
                            <ViewSimpleProperty
                                value={bookingDisambiguation?.dossierNumber}
                                error={booking?.dossierNumber?.toLowerCase() !== bookingDisambiguation?.dossierNumber?.toLowerCase()}
                                label={t('label.dossierNumber')}
                            />
                            {/* Status - subStatus */}
                            <ViewSimpleProperty
                                value={ (!!bookingDisambiguation?.status ? t(`${bookingDisambiguation?.status}`) : '' ) + (!!bookingDisambiguation?.subStatus ? ' - ' + t(`${bookingDisambiguation?.subStatus}`) : '')}
                                error={booking?.status !== bookingDisambiguation?.status || booking?.subStatus !== bookingDisambiguation?.subStatus}
                                label={t('label.status')}
                            />
                            {/* Creation Date Time */}
                            <ViewSimpleProperty
                                value={bookingDisambiguation?.requestCreationDate }
                                error={booking?.requestCreationDate !== bookingDisambiguation?.requestCreationDate}
                                label={t('label.creationDate')}
                            />

                            {/* Total to be paid */}
                            <ViewSimpleProperty
                                value={bookingDisambiguation?.requestUpdateDate}
                                error={booking?.requestUpdateDate !== bookingDisambiguation?.requestUpdateDate}
                                label={t('label.updateDateTime')}
                            />

                            {/* Pickup location */}
                            <ViewSimpleProperty
                                value={bookingDisambiguation?.pickupLocation?.label}
                                error={booking?.pickupLocation?.label !== bookingDisambiguation?.pickupLocation?.label}
                                label={t('label.pickupLocation')}
                            />

                            {/* Pickup date */}
                            <ViewSimpleProperty
                                value={bookingDisambiguation?.pickupDateLocalISO}
                                error={booking?.pickupDateLocalISO !== bookingDisambiguation?.pickupDateLocalISO}
                                label={t('label.pickupDateTime')}
                            />

                            {/* Dropoff location */}
                            <ViewSimpleProperty
                                value={bookingDisambiguation?.dropoffLocation?.label}
                                error={booking?.dropoffLocation?.label !== bookingDisambiguation?.dropoffLocation?.label}
                                label={t('label.dropoffLocation')}
                            />

                            {/* Dropoff date */}
                            <ViewSimpleProperty
                                value={bookingDisambiguation?.dropoffDateLocalISO}
                                error={booking?.dropoffDateLocalISO !== bookingDisambiguation?.dropoffDateLocalISO}
                                label={t('label.dropoffDateTime')}
                            />

                            {/* Total Rental Days */}
                            <ViewSimpleProperty
                                value={bookingDisambiguation?.totalRentalDays}
                                error={booking?.totalRentalDays !== bookingDisambiguation?.totalRentalDays}
                                label={t('label.totalRentalDays')}
                            />

                            {/* Total Rental Days */}
                            <ViewSimpleProperty
                                value={bookingDisambiguation?.flightNumber}
                                error={booking?.flightNumber !== bookingDisambiguation?.flightNumber}
                                label={t('label.flightNumber')}
                            />


                            {/* Mileage Included */}
                            <ViewSimpleProperty
                                value={bookingDisambiguation?.mileageIncluded}
                                error={booking?.mileageIncluded !== bookingDisambiguation?.mileageIncluded}
                                label={t('label.mileageIncluded')}
                            />

                            {/* Total to be paid */}
                            <ViewSimpleProperty
                                value={(!!bookingDisambiguation?.totalToBePaid ? setDecimalSeparator(((parseInt(bookingDisambiguation.totalToBePaid) ?? 0) / 100).toFixed(2), i18n.language) : '0') + ' €'}
                                error={booking?.totalToBePaid !== bookingDisambiguation?.totalToBePaid}
                                label={t('label.totalToBePaid')}
                            />

                            <ViewStructuredProperty
                                value={bookingDisambiguation?.ancillaries}
                                label={t('label.ancillaries')}
                            />

                            <ViewStructuredProperty
                                value={bookingDisambiguation?.firstDriver}
                                label={t('label.mainDriver')}
                            />

                            <ViewStructuredProperty
                                value={bookingDisambiguation?.carGroup}
                                label={t('label.carGroup')}
                            />

                            {/* Notes*/}
                            <Stack px={1} >
                                <Typography>
                                    {t('label.notes')}
                                </Typography>

                                {
                                    bookingDisambiguation?.notes
                                        ?.map((n: any, index: any) => (
                                            <Stack direction="column" pl={4} pt={1}
                                                   sx={{maxWidth: "490px"}}>
                                                <Note key={index} value={n}/>
                                            </Stack>
                                        ))
                                        .reverse()
                                }
                            </Stack>

                        </Stack>
                    </Stack>
                </Paper>
            </Stack>

            <CustomLoader
                loading={
                    getBookingDisambiguation.isLoading ||
                    saveBookingDisambiguationMutation.isLoading ||
                    deleteBookingDisambiguationMutation.isLoading
                }
            />
            <Dialog
                open={acceptDialog.open}
                onClose={acceptDialog.handleClose}
                fullWidth
                maxWidth="sm"
            >
                <DialogCloseButton onClick={acceptDialog.handleClose}/>
                <DialogTitle>
                    {t("label.jimpiDisambiguationViewHeaderTitle")}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {t("message.info.confirm_accept_disambiguation")}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button variant="outlined" onClick={acceptDialog.handleClose}>
                        {t("label.cancel")}
                    </Button>
                     <Button onClick={handleApplyActionClick}>
                        {t("label.confirm")}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={rejectDialog.open}
                onClose={rejectDialog.handleClose}
                fullWidth
                maxWidth="sm"
            >
                <DialogCloseButton onClick={rejectDialog.handleClose}/>
                <DialogTitle>
                    {t("label.jimpiDisambiguationViewHeaderTitle")}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {t("message.info.confirm_reject_disambiguation")}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button variant="outlined" onClick={rejectDialog.handleClose}>
                        {t("label.cancel")}
                    </Button>
                    <Button onClick={handleRejectClick}>
                        {t("label.confirm")}
                    </Button>
                </DialogActions>
            </Dialog>
        </>

    );
}

export default ViewDisambiguation;
